import type { CaptureFileJSONObject } from '@ikegps/core-api';
import { IkeFieldType } from '@ikegps/core-api';
import { IkeAngle, IkeUnit } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField } from '../collection.field';
import { registerFieldType } from './field.registry';
import { CollectionModelFieldNumber } from './number.field';
import { ModelMutatorOrientation, OrientationFieldProvider } from './orientation.field.mutator';
import type { CaptureProvider } from './provider/capture.provider';

export type ModelFieldStateOrientation = ModelFieldStateOrientationManual | ModelFieldStateOrientationBearing;

export interface ModelFieldStateOrientationManual {
	provider: 'manual';
	value: number;
}

export interface ModelFieldStateOrientationBearing {
	provider: 'bearing' | 'backbearing';
	captureId: string;
}

export class CollectionModelFieldOrientation
	extends CollectionModelField<FormModelField, ModelFieldStateOrientation>
	implements CaptureProvider
{
	static readonly PROVIDER_MANUAL = OrientationFieldProvider.MANUAL;
	static readonly PROVIDER_BEARING = OrientationFieldProvider.BEARING;
	static readonly PROVIDER_BACK_BEARING = OrientationFieldProvider.BACK_BEARING;

	static TYPE: IkeFieldType = IkeFieldType.ORIENTATION;

	/** Type guard verify a field is of type orientation */
	static isOrientationField(f: CollectionModelField): f is CollectionModelFieldOrientation {
		return f.schema.type === CollectionModelFieldOrientation.TYPE;
	}

	/**
	 * Read a field that represents an orientation value as degrees.
	 *
	 * Supports both {@link CollectionModelFieldOrientation} and legacy {@link CollectionModelFieldNumber}
	 * so callers that need to handle old collections don't have to branch on field type.
	 *
	 * For both field types: manual values are stored in degrees; bearing/backbearing values are
	 * stored in radians (matching the {@link Bearing} schema), so they need conversion.
	 */
	static readAsDegrees(field: CollectionModelField): number | null {
		if (CollectionModelFieldNumber.isNumberField(field) || CollectionModelFieldOrientation.isOrientationField(field)) {
			if (field.value == null) {
				return null;
			}
			if (field.isManual()) {
				return field.value;
			}
			if (field.isBearing() || field.isBackBearing()) {
				return IkeAngle.toDegrees(field.value);
			}
		}

		return null;
	}

	private _Mutator: ModelMutatorOrientation = new ModelMutatorOrientation(this);
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorOrientation(this);
		}
		return this._Mutator;
	}

	/**
	 * Calculate the value of the orientation field based off the provider type.
	 * @returns Manual values in degrees [0, 360); bearing/backbearing values in radians [0, 2π).
	 */
	override get value(): number | null {
		if (this.stateValue == null) {
			return null;
		}

		if (this.isManual()) {
			return this.stateValue.value;
		}

		if (this.isBackBearing()) {
			const capture = this.collection.state.getCapture(this.stateValue.captureId);
			return IkeAngle.normalize(capture.orientation.bearing + Math.PI);
		}

		if (this.isBearing()) {
			const capture = this.collection.state.getCapture(this.stateValue.captureId);
			return IkeAngle.normalize(capture.orientation.bearing);
		}

		return null;
	}

	/** Display the orientation value in degrees (UI + importable Excel: 3dp per UFO-121) */
	override get displayValue(): string {
		return this.formatDegrees(IkeUnit.ANGLE_DECIMALS);
	}

	/** Other exports (KML, standard Excel, PDF) show angles with 2dp per UFO-649 */
	override get exportDisplayValue(): string {
		return this.formatDegrees(IkeUnit.DECIMALS);
	}

	private formatDegrees(decimals: number): string {
		if (this.isManual()) {
			if (isNaN(this.value) || typeof this.value !== 'number') {
				return '';
			}
			return String(IkeUnit.round(this.value, decimals));
		}

		if (this.isBackBearing() || this.isBearing()) {
			const degrees = IkeUnit.convertAngle(this.value, IkeUnit.DEGREE, IkeUnit.RADIAN);
			return String(IkeUnit.round(degrees, decimals));
		}

		return '';
	}

	/**
	 * Get the provider type of the field.
	 * Defaults to 'manual' if no provider is defined.
	 */
	get provider(): ModelFieldStateOrientation['provider'] {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return CollectionModelFieldOrientation.PROVIDER_MANUAL;
		}
		return stateValue.provider;
	}

	isManual(): this is CollectionModelField<FormModelField, ModelFieldStateOrientationManual> {
		return this.provider === CollectionModelFieldOrientation.PROVIDER_MANUAL;
	}

	isBearing(): this is CollectionModelField<FormModelField, ModelFieldStateOrientationBearing> {
		return this.provider === CollectionModelFieldOrientation.PROVIDER_BEARING;
	}

	isBackBearing(): this is CollectionModelField<FormModelField, ModelFieldStateOrientationBearing> {
		return this.provider === CollectionModelFieldOrientation.PROVIDER_BACK_BEARING;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	/** All captures used by this field. */
	getCaptures(): CaptureFileJSONObject[] {
		return this.getCaptureIds()
			.map((c) => this.collection.state.getCapture(c))
			.filter((c) => !!c);
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}

	getCaptureIds(): string[] {
		if (this.isManual()) {
			return [];
		}

		if (this.stateValue == null) {
			return [];
		}

		if (this.isBackBearing() || this.isBearing()) {
			return [this.stateValue.captureId];
		}
		return [];
	}

	/** Whether this field's value is valid */
	hasValue(): boolean {
		const value = this.value;
		if (value == null || isNaN(value)) {
			return false;
		}
		return true;
	}
}

registerFieldType(IkeFieldType.ORIENTATION, (parent, schema) => new CollectionModelFieldOrientation(parent, schema));
